import FaceThumbnail from '@src/components/sidepanel/modals/FaceThumbnail';
import React, { useState } from 'react';
import styled from 'styled-components';

import ItemDropdownSelect from '../../common/ItemDropdownSelect';
import { AssignTranscriptSpeaker } from './AssignTranscriptSpeaker';

import type { ReframeSpeakersTabView } from '@src/hooks/useReframeSpeakersTab';

const UNASSIGNED = '__unassigned__';
const ASSIGN_PREFIX = 'assign:';

/**
 * The reframe modal's Speakers tab (PLAT-11992): each face in the scene
 * being framed, and who it is. Picking a speaker saves the face to the story
 * and reframes the scenes it affects; a transcript speaker not assigned yet
 * is assigned first — from the story, the showcase, or a new speaker.
 */
export function ReframeSpeakersTab({ view }: { view: ReframeSpeakersTabView }) {
  const [pendingAssign, setPendingAssign] = useState<{
    faceLabel: string;
    speakerLabel: string;
  } | null>(null);

  const named = view.speakers.filter(speaker => speaker.isNamed);
  const unnamed = view.speakers.filter(speaker => !speaker.isNamed);
  const options = [
    { caption: 'Unassigned', value: UNASSIGNED },
    ...named.map(speaker => ({
      caption: speaker.name,
      value: speaker.speakerLabel,
    })),
    ...(unnamed.length > 0
      ? [
          {
            caption: 'Not Yet Assigned',
            value: '__heading__',
            isHeading: true,
          },
          ...unnamed.map(speaker => ({
            caption: `Assign ${speaker.name}…`,
            value: `${ASSIGN_PREFIX}${speaker.speakerLabel}`,
            isAction: true,
          })),
        ]
      : []),
  ];

  const handleSelect = (faceLabel: string, value: string | null) => {
    if (!value) return;
    if (value.startsWith(ASSIGN_PREFIX)) {
      setPendingAssign({
        faceLabel,
        speakerLabel: value.slice(ASSIGN_PREFIX.length),
      });
      return;
    }
    void view.assign(faceLabel, value === UNASSIGNED ? null : value);
  };

  return (
    <Container>
      <Hint>
        Who is each face in this scene? Changes are saved to the story and
        reframe the scenes they affect.
      </Hint>
      {view.isLoading ? (
        <Hint>Loading faces…</Hint>
      ) : view.faces.length === 0 ? (
        <Hint>No faces detected in this scene.</Hint>
      ) : (
        <FaceList>
          {view.faces.map(face => (
            <FaceRow key={face.faceLabel}>
              <Avatar>
                {face.thumbnail && (
                  <FaceThumbnail
                    videoUrl={view.videoUrl}
                    time={face.thumbnail.time}
                    box={face.thumbnail.box}
                    size={44}
                    cacheKey={`${view.videoUrl}:${face.faceLabel}`}
                    label={face.faceLabel}
                  />
                )}
              </Avatar>
              <Picker>
                <ItemDropdownSelect
                  values={options}
                  propertyName={face.faceLabel}
                  defaultValue={face.speakerLabel ?? UNASSIGNED}
                  action={handleSelect}
                />
              </Picker>
            </FaceRow>
          ))}
        </FaceList>
      )}
      {pendingAssign && (
        <AssignTranscriptSpeaker
          speakerLabel={pendingAssign.speakerLabel}
          speakerName={
            view.speakers.find(
              speaker => speaker.speakerLabel === pendingAssign.speakerLabel,
            )?.name ?? pendingAssign.speakerLabel
          }
          onClose={() => setPendingAssign(null)}
          onAssigned={datoSpeakerId => {
            setPendingAssign(null);
            void view.assignAfterLinking(
              pendingAssign.faceLabel,
              pendingAssign.speakerLabel,
              datoSpeakerId,
            );
          }}
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  width: 100%;
  min-height: 240px;
  /* ItemDropdownSelect's trigger inherits its text colour. */
  color: var(--foreground);
`;

const Hint = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
`;

const FaceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const FaceRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
`;

/** One width for every row's picker, whatever the selected name's length. */
const Picker = styled.div`
  width: 260px;
`;

/** People are circles (design system, Icon Semantics). */
const Avatar = styled.div`
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface);
`;
